import {
  computed, ref, onUnmounted, watch,
} from '@vue/composition-api'

import useRequest from '@/compositions/useRequest'
import useWialon from '../../../../compositions/useWialon'

import Skeleton from '@/components/Skeleton/Skeleton'
import InstanceView from '@/components/InstanceView/InstanceView'
import EventsView from '@/components/EventsView'
import OrderCancelDialog from '@/components/OrderCancelDialog/OrderCancelDialog'

import Progress from '@/components/@order/Progress'
import TechniqueDescription from '@/components/TechniqueDescription/TechniqueDescription.vue'
import LinkDecorated from '@/components/@ui/LinkDecorated'
import { statesToColor } from '@/constants/orders'
import {
  min, formatTime, minTime,
} from '@/utils/formatDate'
import Messages from './components/Messages/Messages'
import Documents from './components/Documents/Documents'
import Events from './components/Events/Events'
import Calendar from './components/Calendar/Calendar'
import ReportPopup from './components/ReportPopup/ReportPopup'
import Workers from './components/Workers/Workers'
import WorkersPopUp from './components/WorkersPopUp/WorkersPopUp'
import SetDriverPopup from '@/components/SetDriverPopup/SetDriverPopup'
import RentalExtensionPopup from '@/components/RentalExtensionPopup/RentalExtensionPopup'
import OrderReviewAlert from './components/OrderReviewAlert/OrderReviewAlert.vue'


const AVAILABLE_TYPE = [
  {
    name: 'Мониторинг', component: InstanceView,
  },
  {
    name: 'События', component: Events,
  },
  {
    name: 'Документы', component: Documents,
  },
  {
    name: 'Календарь', component: Calendar,
  },
  {
    name: 'Сообщения', component: Messages,
  },
]

export default ({
  components: {
    Skeleton,
    InstanceView,
    EventsView,
    OrderCancelDialog,
    Messages,
    Documents,
    Events,
    Calendar,
    ReportPopup,
    Progress,
    TechniqueDescription,
    LinkDecorated,
    SetDriverPopup,
    RentalExtensionPopup,
    Workers,
    WorkersPopUp,
    OrderReviewAlert,
  },
  setup(props, context) {
    const { $store, $mq, $access } = context.root
    const currentTab = ref(0)
    const popupReport = ref(false)
    const popupCloseOrder = ref(false)
    const popupChangeDriver = ref(false)
    const popupExtension = ref(false)
    const popupWorkers = ref(false)
    const workers = ref([])
    const orderId = ref(null)
    const order = ref(null)
    const reports = computed(() => order?.value?.reports)
    const reportParams = computed(() => order?.value?.reportParams)
    const type = computed(() => AVAILABLE_TYPE[currentTab.value])
    const wialonConnection = computed(() => $store.state.auth.wialonConnection)
    const userRole = computed(() => $store.getters['auth/userRole'])
    const isActiveWialonSession = ref(false)
    const reviewOrderId = ref(null)
    const { initWialon, closeSession } = useWialon
    const renewalRequest = computed(() => {
      if (order.value?.renewalRequest?.state === 'new') return order.value?.renewalRequest

      return false
    })
    const showExtensionBtn = computed(() => $access('canRenew') && order.value?.isRenewal)
    const showSubmitRenewal = computed(() => $access('canSubmitRenew') && renewalRequest)
    const DATE_NOW = new Date().toISOString().slice(0, 10)
    const newDateNow = new Date(DATE_NOW).getTime()
    const extensionType = computed(() => (order.value?.dateStart === order.value?.dateEnd ? 'hours' : 'days'))
    const routePoints = computed(() => {
      if (!order.value?.instance?.monitoring) return null
      const { lat: instanceX, lon: instanceY } = order.value?.instance?.monitoring
      const { lat: orderX, lon: orderY } = order.value
      return [[instanceY, instanceX], [orderY, orderX]]
    })
    const userCompanyId = computed(() => $store.getters['auth/companyId'])

    const orderRequest = useRequest({
      context,
      request: id => $store.dispatch('orders/fetchOrder', { id }),
    })

    const getWorkers = useRequest({
      context,
      request: ({ page, per }) => $store.dispatch('data/fetchWorkers', { page, per }),
    })
    const isAvailableDates = computed(() => newDateNow >= new Date(order.value.dateStart).getTime() && newDateNow <= new Date(order.value.dateEnd).getTime())

    const getProps = computed(() => ({
      orderId: Number(orderId.value),
      orderOwner: order.value.owner?.id,
      orderDriver: order.value.driver,
      groupedMachineHours: order.value.groupedMachineHours,
      orderDates: [order.value.dateStart, order.value.dateEnd],
      id: Number(orderId.value),
      instance: order.value.instance,
      topInfo: true,
      showRoute: true,
      visit: order.value.visit,
      orderRadius: order.value.areaSize,
      points: routePoints.value,
      disabled: !order.value.canBeDone,
      available: isAvailableDates.value,
      depressed: true,
      class: $mq !== 'sm' ? 'mt-d2' : '',
    }))

    const init = async () => {
      const { id } = context.root.$route.params
      orderId.value = Number(id)
      order.value = await orderRequest.makeRequest(id)
      if (userCompanyId !== order.ownerId) {
        const response = await getWorkers.makeRequest({ page: 1, per: -1 })
        workers.value = response.employee
      }
      if (order.value.wialonToken && isAvailableDates.value) {
        isActiveWialonSession.value = true
        initWialon(order.value.wialonToken)
      }
    }
    const closeExtensionDialog = () => {
      popupExtension.value = false
    }

    const updateDriver = () => {
      popupChangeDriver.value = false
      init()
    }
    const updateExtension = () => {
      closeExtensionDialog()
      init()
    }
    const updateOrder = () => {
      init()
    }
    const getClientName = () => {
      if (!order.value.client) return order.value.myOwnerName
      const {
        lastName = '', firstName = '', middleName = '', name = '',
      } = order.value.client
      let str = ''
      if (lastName) str += `${lastName} `
      if (firstName) str += `${firstName} `
      if (middleName) str += middleName
      if (str === '') str += name
      return str
    }

    init()


    const acceptRequest = useRequest({
      context,
      successMessage: 'Запрос принят!',
      request: () => $store.dispatch('orders/acceptRenewal', { id: renewalRequest.value.id }),
    })

    const rejectRequest = useRequest({
      context,
      successMessage: 'Запрос отклонен!',
      request: () => $store.dispatch('orders/rejectRenewal', { id: renewalRequest.value.id }),
    })

    const renewalSubmitAction = async (submit = 'accept') => {
      if (submit === 'accept') {
        await acceptRequest.makeRequest()
      } else {
        await rejectRequest.makeRequest()
      }

      await init()
    }

    const orderStatus = computed(() => {
      const orderState = order.value.state

      if (orderState === 'new' || orderState === 'tender') {
        return `Подбор техники\n - ${order.value.instancesCount} ед.`
      }
      return order.value.humanStateName
    })
    const handleShowWorkersPopUp = () => {
      popupWorkers.value = true
    }
    const handleCloseWorkersPopUp = () => {
      popupWorkers.value = false
    }
    const updateOrderData = (updatedOrderData) => {
      order.value = updatedOrderData
    }
    const getStatusColor = state => statesToColor[state] || 'warning'

    const orderShiftStatus = computed(() => {
      let color = ''
      let text = ''
      const shiftStatus = order.value.shiftState

      if (shiftStatus === 'active') {
        color = 'green'
        text = 'В работе'
      } else if (shiftStatus === 'break') {
        color = 'orange'
        text = 'Перерыв'
      } else if (!shiftStatus || shiftStatus === 'inactive') {
        color = 'grey'
        text = 'Не активен'
      }

      return {
        color,
        text,
      }
    })

    const orderWorkers = computed(() => {
      if (userRole.value === 'owner') {
        return order.value ? order.value.ownerAddedEmployees : []
      }
      return order.value ? order.value.clientAddedEmployees : []
    })

    watch(() => $store.state.data.reviewOrderIds, (orderIds) => {
      reviewOrderId.value = orderIds.find(el => el === order.value.id)
    })

    watch(() => $store.state.orders.order, (data) => {
      order.value = data
    })

    onUnmounted(() => {
      if (isActiveWialonSession.value) {
        closeSession()
      }
    })


    return {
      AVAILABLE_TYPE,
      currentTab,
      type,
      getProps,
      order,
      loading: orderRequest.loading,
      popupReport,
      popupCloseOrder,
      popupChangeDriver,
      popupExtension,
      reports,
      reportParams,
      orderId,
      min,
      formatTime,
      minTime,
      userCompanyId,
      showExtensionBtn,
      extensionType,
      showSubmitRenewal,
      renewalRequest,
      orderStatus,
      orderShiftStatus,
      wialonConnection,
      popupWorkers,
      workers,
      userRole,
      orderWorkers,
      reviewOrderId,
      closeExtensionDialog,
      handleShowWorkersPopUp,
      handleCloseWorkersPopUp,
      updateDriver,
      updateExtension,
      getClientName,
      getStatusColor,
      renewalSubmitAction,
      updateOrderData,
      updateOrder,
    }
  },
})
